import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useBranding } from "@/context/BrandingContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { SmartLink } from "../components/Primitives";
import { CONTAINER, HEADER_MUTED } from "../tokens";

const NAV_LINK = "text-[11px] tracking-[0.22em] uppercase pb-1 border-b transition-colors whitespace-nowrap";
// Couleurs : fond et textes suivent les couleurs de ZONE « En-tête » du
// Studio (header-bg / header-text / header-muted, voir tailwind.config.js),
// qui retombent sur `cream` / `ink` / `graphite` tant que l'admin n'a rien
// choisi (le logotype secondaire retombe sur `ash`, sa couleur du handoff).
const CTA = "inline-flex items-center text-[10px] tracking-[0.22em] uppercase bg-header-text text-header-bg py-[13px] px-[22px] hover:bg-blush hover:text-cream transition-colors whitespace-nowrap";

// En-tête du handoff : bandeau COLLANT, fond crème translucide flouté
// (8px), padding 20px 28px. Sur desktop, DEUX rangées séparées d'un filet
// ink/12 % : le logotype seul, centré (« AEF » Cormorant 28px .16em +
// « RETOUCHES » Jost 10px .42em — le logo téléversé dans Studio > Identité
// de marque s'affiche À CÔTÉ du logotype, jamais à sa place : une image de
// logo ne porte pas forcément le nom de la maison), puis les liens de
// navigation centrés, soulignés d'un filet quand actifs ou survolés, suivis
// du sélecteur de langue et du bouton plein « Nous contacter ».
//
// Sous md, une seule barre compacte : burger à gauche, logo au centre,
// sélecteur de langue (et bouton d'appel à partir de sm) à droite — le
// handoff ne dessine pas le mobile : « prévoir un menu compact ». Sous sm le
// bouton d'appel quitte la barre et rejoint le panneau déroulant, pour que
// la barre tienne sur UNE ligne — même règle que l'en-tête du moteur
// "agence_v1".
export default function SiteHeader({ props = {}, blocks = [] }) {
  const branding = useBranding();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const items = (blocks || [])
    .filter((b) => b.type === "menu_item" && b.enabled !== false && b.props?.label && b.props?.url)
    .map((b) => ({ id: b.id, label: b.props.label, url: b.props.url }));
  const isActive = (url) => url.split("#")[0].replace(/\/$/, "") === pathname.replace(/\/$/, "");
  const name = branding.loaded ? branding.name || "" : "";
  // Style d'en-tête (Studio > En-tête > Style d'en-tête) : "stacked" par
  // défaut (deux rangées, voir ci-dessus) ou "classic" (barre unique du
  // handoff : logotype à gauche, liens, langue et bouton à droite).
  const stacked = props.header_layout !== "classic";
  const navLinks = items.map((it) => (
    <SmartLink
      key={it.id}
      url={it.url}
      aria-current={isActive(it.url) ? "page" : undefined}
      className={`${NAV_LINK} text-header-text ${isActive(it.url) ? "border-header-text/25" : "border-transparent hover:border-header-text/25"}`}
    >
      {it.label}
    </SmartLink>
  ));

  return (
    <header className="sticky top-0 z-40 bg-header-bg/[0.94] backdrop-blur-[8px] border-b border-header-text/[0.12]">
      {/* Rangée 1 — mobile : burger / logo / actions ; desktop : logo seul
          centré ("stacked") ou barre complète ("classic"). */}
      <div className={`${CONTAINER} px-5 md:px-7 py-4 ${stacked ? "md:py-6 md:justify-center" : "md:py-5 md:gap-6 md:flex-wrap"} flex items-center gap-3 sm:gap-5`}>
        {(items.length > 0 || props.cta_label) && (
          <button
            type="button"
            aria-label="Menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden shrink-0 h-[42px] w-[42px] border border-header-text/20 flex flex-col items-center justify-center gap-[5px]"
          >
            <span className="w-[18px] h-px bg-header-text" />
            <span className="w-[18px] h-px bg-header-text" />
            <span className="w-[18px] h-px bg-header-text" />
          </button>
        )}

        <Link
          to="/"
          className={`flex-1 min-w-0 flex items-center gap-3 md:gap-4 leading-none no-underline ${stacked ? "md:flex-none justify-center" : "md:flex-none md:me-auto justify-center md:justify-start"}`}
          aria-label={name || props.wordmark || "Accueil"}
        >
          {branding.logo_url && (
            <img src={branding.logo_url} alt={name} className={`h-[34px] ${stacked ? "md:h-[40px]" : ""} w-auto max-w-[160px] object-contain shrink-0`} />
          )}
          {(props.wordmark || props.wordmark_sub) && (
            <span className={`block min-w-0 ${branding.logo_url || !stacked ? "text-start" : "text-center"}`}>
              {props.wordmark && (
                <span className={`block font-display font-normal text-[28px] ${stacked ? "md:text-[32px]" : ""} tracking-[0.16em] text-header-text truncate`}>{props.wordmark}</span>
              )}
              {props.wordmark_sub && (
                <span
                  className="block text-[10px] tracking-[0.42em] uppercase mt-1 truncate"
                  style={HEADER_MUTED}
                >
                  {props.wordmark_sub}
                </span>
              )}
            </span>
          )}
        </Link>

        {!stacked && (
          <nav className="hidden md:flex items-center gap-7 flex-wrap" aria-label="Navigation principale">
            {navLinks}
          </nav>
        )}

        {/* Actions : sur mobile, au moins la largeur du burger pour garder le
            logo centré même sans sélecteur de langue ni bouton. En "stacked",
            elles rejoignent la seconde rangée sur desktop. */}
        <div className={`${stacked ? "md:hidden" : ""} flex items-center justify-end gap-3 sm:gap-5 md:gap-6 shrink-0 min-w-[42px] md:min-w-0`}>
          <LanguageSwitcher className="shrink-0" linkClass="text-header-text/[0.66] hover:text-header-text" />
          {props.cta_label && (
            <SmartLink url={props.cta_url || "/professionnels-contact#contact"} className={`hidden sm:inline-flex ${CTA}`}>
              {props.cta_label}
            </SmartLink>
          )}
        </div>
      </div>

      {/* Rangée 2 — desktop, "stacked" : filet, puis menu + langue + bouton, centrés. */}
      {stacked && (items.length > 0 || props.cta_label) && (
        <div className="hidden md:block border-t border-header-text/[0.12]">
          <div className={`${CONTAINER} px-7 py-5 flex items-center justify-center gap-x-8 lg:gap-x-10 gap-y-3 flex-wrap`}>
            <nav className="contents" aria-label="Navigation principale">
              {navLinks}
            </nav>
            <LanguageSwitcher className="shrink-0" linkClass="text-header-text/[0.66] hover:text-header-text" />
            {props.cta_label && (
              <SmartLink url={props.cta_url || "/professionnels-contact#contact"} className={CTA}>
                {props.cta_label}
              </SmartLink>
            )}
          </div>
        </div>
      )}

      {open && (
        <div className="md:hidden flex flex-col px-5 pb-5 border-t border-header-text/10">
          {items.map((it) => (
            <SmartLink
              key={it.id}
              url={it.url}
              onClick={() => setOpen(false)}
              className="text-[11px] tracking-[0.22em] uppercase text-header-text py-3.5 border-b border-header-text/[0.08]"
            >
              {it.label}
            </SmartLink>
          ))}
          {props.cta_label && (
            <SmartLink
              url={props.cta_url || "/professionnels-contact#contact"}
              onClick={() => setOpen(false)}
              className={`sm:hidden mt-4 justify-center ${CTA}`}
            >
              {props.cta_label}
            </SmartLink>
          )}
        </div>
      )}
    </header>
  );
}
